import React, { FC } from "react";
import { Pagination, Skeleton, Button, Empty, Tooltip } from "antd";
import {
  PlusOutlined,
  RightOutlined,
  LockOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { AddAccess } from "../../../../components/accessControl/AccessControl";
import { StatusPill } from "./statusMeta";
import MilestoneRail from "./MilestoneRail";

const initialsOf = (name?: string) => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
};

const fmtDate = (d?: string) => (d ? dayjs(d).format("DD MMM YYYY") : "—");

interface ProjectListRowsProps {
  dataSource: any[];
  loading: boolean;
  pagination: any;
  statusTab: string;
  onOpen: (record: any) => void;
  onPageChange: (page: number, pageSize: number) => void;
  onAddProject: () => void;
}

const ProjectListRows: FC<ProjectListRowsProps> = ({
  dataSource,
  loading,
  pagination,
  statusTab,
  onOpen,
  onPageChange,
  onAddProject,
}) => {
  if (loading) {
    return (
      <div className="pms-list-rows">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="pms-list-row" style={{ cursor: "default" }}>
            <Skeleton active paragraph={false} title={{ width: "80%" }} />
          </div>
        ))}
      </div>
    );
  }

  if (!dataSource?.length) {
    return (
      <div className="pms-hub-empty">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={`No ${statusTab === "All" ? "" : statusTab + " "}projects`}
        >
          <AddAccess moduleName="Projects">
            <Button type="primary" icon={<PlusOutlined />} onClick={onAddProject}>
              Add Project
            </Button>
          </AddAccess>
        </Empty>
      </div>
    );
  }

  return (
    <>
      <div className="pms-list-rows pms-view-fade">
        {dataSource.map((record: any) => (
          <div
            key={record.uid}
            className="pms-list-row"
            onClick={() => onOpen(record)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") onOpen(record);
            }}
          >
            <div className="pms-row-main">
              {record?.projectGroupName && (
                <div className="pms-eyebrow">{record.projectGroupName}</div>
              )}
              <div className="pms-row-title" title={record?.projectTitle}>
                {record?.lock && (
                  <Tooltip
                    title={`Locked by ${record?.lockedByUserName || "N/A"}`}
                  >
                    <LockOutlined
                      style={{ color: "#8094ae", marginRight: 6, fontSize: 12 }}
                    />
                  </Tooltip>
                )}
                {record?.projectTitle}
              </div>
              <div style={{ marginTop: 6 }}>
                <StatusPill status={record?.projectStatus} />
              </div>
            </div>

            <div className="pms-row-people">
              <span className="pms-avatar">
                {initialsOf(record?.projectOwnerName)}
              </span>
              <span className="pms-meta-text">
                {record?.projectOwnerName || "Unassigned"}
              </span>
              {record?.siteName && (
                <>
                  <EnvironmentOutlined
                    className="pms-meta-icon"
                    style={{ marginLeft: 8 }}
                  />
                  <span className="pms-meta-text">{record.siteName}</span>
                </>
              )}
            </div>

            <div className="pms-row-progress">
              <MilestoneRail
                completed={record?.completedPhases}
                total={record?.totalPhases}
                status={record?.projectStatus}
              />
              <div
                className="pms-figure-caption"
                style={{ marginTop: 6, textTransform: "none", letterSpacing: 0 }}
              >
                {record?.completedPhases ?? 0}/{record?.totalPhases ?? 0} phases
                · {Math.round(Number(record?.tasksCompletionPercentage) || 0)}%
                tasks
              </div>
            </div>

            <div className="pms-row-dates">
              <div>{fmtDate(record?.plannedStartDate)}</div>
              <div>{fmtDate(record?.plannedEndDate)}</div>
            </div>

            <RightOutlined className="pms-row-chevron" />
          </div>
        ))}
      </div>
      <div className="pms-hub-pagination">
        <Pagination
          size="small"
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger={pagination.showSizeChanger}
          pageSizeOptions={pagination.pageSizeOptions}
          onChange={onPageChange}
          showTotal={(t: number) => `Total ${t} projects`}
        />
      </div>
    </>
  );
};

export default ProjectListRows;
